import React from "react";
import { useTheme } from "../context/ThemeContext";

function TradeHistory({ trades, calculateProfit, onEditTrade, onDeleteTrade }) {
  const { darkMode } = useTheme();

  const calculatePips = (trade) => {
    const entry = parseFloat(trade.entryPrice);
    const tp = parseFloat(trade.takeProfit);
    const sl = parseFloat(trade.stopLoss);

    if (trade.entryType === "buy") {
      if (trade.outcome === "tp") {
        // Buy win: TP - Entry
        return (tp - entry) * 100;
      } else {
        // Buy loss: Entry - SL
        return (entry - sl) * 100;
      }
    } else {
      // Sell trade
      if (trade.outcome === "tp") {
        // Sell win: Entry - TP
        return (entry - tp) * 100;
      } else {
        // Sell loss: SL - Entry
        return (sl - entry) * 100;
      }
    }
  };

  return (
    <div
      className={`${
        darkMode ? "bg-gray-800" : "bg-white"
      } rounded-lg shadow-md`}
    >
      <div className="p-6">
        <h2
          className={`text-xl font-semibold mb-4 ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Trade History
        </h2>
        <div className="w-full">
          <table className="min-w-full table-auto">
            <thead>
              <tr
                className={`text-sm ${darkMode ? "bg-gray-700" : "bg-gray-50"}`}
              >
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Date
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Type
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Entry
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  TP
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  SL
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Lot Size
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Outcome
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Pips
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Profit/Loss
                </th>
                <th
                  className={`px-3 py-2 text-center font-medium ${
                    darkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade, index) => (
                <tr
                  key={index}
                  className={`text-sm border-t ${
                    darkMode ? "border-gray-700" : "border-gray-200"
                  } hover:bg-opacity-50 ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }`}
                >
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.date}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.entryType.toUpperCase()}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.entryPrice}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.takeProfit}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.stopLoss}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {trade.lotSize}
                  </td>
                  <td className={`px-3 py-2 text-center`}>
                    <span
                      className={`${
                        trade.outcome === "tp"
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {trade.outcome === "tp" ? "TP" : "SL"}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {calculatePips(trade).toFixed(0)}
                  </td>
                  <td
                    className={`px-3 py-2 text-center ${
                      calculateProfit(trade) >= 0
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    ${calculateProfit(trade).toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onEditTrade(index)}
                        className={`p-1 rounded hover:bg-opacity-80 transition-colors duration-150 ${
                          darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                        }`}
                        title="Edit trade"
                      >
                        <svg
                          className={`w-4 h-4 ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          } hover:text-blue-500`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteTrade(index)}
                        className={`p-1 rounded hover:bg-opacity-80 transition-colors duration-150 ${
                          darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                        }`}
                        title="Delete trade"
                      >
                        <svg
                          className={`w-4 h-4 ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          } hover:text-red-500`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TradeHistory;
